import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  DrawerContent,
  DrawerActions,
  FormCheckbox,
  Input,
  Button,
  ButtonVariant,
  Select,
} from "@industriousoffice/react-components";
import {
  Account,
  Location,
  ZuoraPaymentMethod,
  ZuoraPaymentMethodTypes,
} from "@/api/opb/opbTypes";
import { SepaCountries } from "@/utils/location";
import { PaymentConfirmationDialog as ConfirmationDialog } from "./PaymentConfirmationDialog";
import { PaymentDrawerTitle as DrawerTitle } from "./PaymentDrawerTitle";
import {
  PaymentFormInput as FormInput,
  PaymentFormSelect as FormSelect,
} from "./PaymentFormInput";
import { PaymentDrawer as Drawer } from "./PaymentDrawer";
import {
  editSavedPaymentMethodSchema,
  SavedPaymentMethodValues,
} from "../schemas/EditSavedPaymentMethodSchema";
import { paymentMethodLabel } from "./PaymentMethodCard";
import FormExpirationDateInput from "./FormExpirationDateInput";
import { CardFeeNotice } from "./CardFeeNotice";

import { isExpiredCard } from "../utils/payment-method-settings";

interface PaymentMethodEditorProps {
  method: ZuoraPaymentMethod;
  account: Account;
  location?: Location;
  methods: ZuoraPaymentMethod[];
  isBusy: boolean;
  isReadBlocked?: boolean;
  error?: Error;
  isOpen: boolean;
  onAfterClose: () => void;
  onClose: () => void;
  onSave: (values: SavedPaymentMethodValues) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  onMakeDefault: (id: string) => void;
  onTurnOffAutoPay: () => void;
}

export function PaymentMethodEditor({
  method,
  account,
  location,
  methods,
  isBusy,
  isReadBlocked = false,
  error,
  isOpen,
  onAfterClose,
  onClose,
  onSave,
  onRemove,
  onMakeDefault,
  onTurnOffAutoPay,
}: PaymentMethodEditorProps): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const card =
    method.type === ZuoraPaymentMethodTypes.CreditCard ? method : undefined;
  const isAch = method.type === ZuoraPaymentMethodTypes.ACH;
  const isPad = method.type === ZuoraPaymentMethodTypes.Pad;
  const isSepa = method.type === ZuoraPaymentMethodTypes.Sepa;
  const isDefault = account.defaultPaymentMethodId === method.id;
  const isOnlyMethod = methods.length === 1;
  const [confirmation, setConfirmation] = useState<
    "discard" | "save" | "remove"
  >();
  const [replacement, setReplacement] = useState(
    methods.find((value) => value.id !== method.id && !isExpiredCard(value))
      ?.id || ""
  );
  const [pendingValues, setPendingValues] =
    useState<SavedPaymentMethodValues>();
  const holder = method.accountHolderInfo;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<SavedPaymentMethodValues>({
    mode: "onChange",
    resolver: yupResolver(editSavedPaymentMethodSchema(method)),
    defaultValues: {
      nickname: method.nickname || "",
      defaultPayment: isDefault,
      expirationDate:
        card == null
          ? ""
          : `${String(card.expMonth).padStart(2, "0")}/${card.expYear}`,
      cvv: "",
      accountHolderInfo: {
        addressLine1: holder?.addressLine1 || "",
        addressLine2: holder?.addressLine2 || "",
        email: holder?.email || "",
        city: holder?.city || "",
        zipCode: holder?.zipCode || "",
        state: holder?.state || "",
        phone: holder?.phone || "",
        country: holder?.country || "",
      },
    },
  });
  useEffect(() => {
    setValue("defaultPayment", isDefault, { shouldDirty: false });
  }, [isDefault, setValue]);
  const close = () => {
    if (isBusy) return;
    if (isDirty) setConfirmation("discard");
    else onClose();
  };
  const save = async (values: SavedPaymentMethodValues) => {
    setConfirmation(undefined);
    if (await onSave(values)) onClose();
  };
  const submit = handleSubmit((values) => {
    if (values.defaultPayment && !isDefault && account.autopay) {
      setPendingValues(values);
      setConfirmation("save");
    } else save(values);
  });
  let number = card?.cardNumber || "";
  if (method.type === ZuoraPaymentMethodTypes.ACH)
    number = method.bankAccountNumber;
  if (
    method.type === ZuoraPaymentMethodTypes.Pad ||
    method.type === ZuoraPaymentMethodTypes.Bacs
  )
    number = method.accountNumber;
  if (method.type === ZuoraPaymentMethodTypes.Sepa)
    number = method.IBAN || method.accountNumber;
  const label = paymentMethodLabel(method);
  const blockRemoval =
    (isDefault && !isOnlyMethod) || (isOnlyMethod && account.autopay);

  let editableDescription =
    "You can edit nickname and contact details. Payment identifiers and holder name are read-only.";
  if (card != null)
    editableDescription =
      "You can edit expiry, CVV and nickname. Card number and name are read-only.";
  else if (isAch)
    editableDescription =
      "You can edit the nickname. Bank details and holder name are read-only.";
  let confirmationTitle = "Change your Auto-Pay payment method?";
  let confirmationLabel = "Save and use this method";
  if (confirmation === "discard") {
    confirmationTitle = "Discard your changes?";
    confirmationLabel = "Discard changes";
  }
  if (confirmation === "remove") {
    confirmationTitle = "Remove this payment method?";
    confirmationLabel = "Remove payment method";
  }
  return (
    <>
      <Drawer
        isOpen={isOpen}
        onAfterClose={onAfterClose}
        onClose={close}
        className="payment-drawer z-10"
      >
        <DrawerTitle title={`${t("Edit")} ${label}`} onClose={close}>
          <p className="payment-drawer-context">
            {location?.name} · {account.name}
          </p>
        </DrawerTitle>
        <form onSubmit={submit} className="payment-editor-form">
          <DrawerContent>
            <fieldset
              disabled={isBusy || isReadBlocked}
              className="payment-edit-fieldset"
            >
              <p className="payment-editor-intro">{t(editableDescription)}</p>
              <div className="payment-edit-fields">
                <Input
                  label={t(card == null ? "Account Number" : "Card number")}
                  aria-label={t(
                    card == null ? "Account Number" : "Card number"
                  )}
                  value={`•••• ${number.slice(-4)}`}
                  readOnly
                />
                {card != null && (
                  <div className="payment-field-pair">
                    <FormExpirationDateInput
                      control={control}
                      name="expirationDate"
                      label={t("Expiration date")}
                      isRequired
                    />
                    <FormInput
                      control={control}
                      name="cvv"
                      label={t("CVV / CVC (optional)")}
                      type="password"
                      autoComplete="cc-csc"
                    />
                  </div>
                )}
                {method.type === ZuoraPaymentMethodTypes.ACH && (
                  <>
                    <Input
                      label={t("ABA/routing number")}
                      aria-label={t("ABA/routing number")}
                      value={method.bankABACode}
                      readOnly
                    />
                    <Input
                      label={t("Account type")}
                      aria-label={t("Account type")}
                      value={method.bankAccountType}
                      readOnly
                    />
                    <Input
                      label={t("Bank name")}
                      aria-label={t("Bank name")}
                      value={method.bankName}
                      readOnly
                    />
                  </>
                )}
                {isPad && (
                  <div className="payment-field-pair">
                    <Input
                      label={t("Bank Code / Transit Number")}
                      aria-label={t("Bank Code / Transit Number")}
                      value={method.bankCode}
                      readOnly
                    />
                    <Input
                      label={t("Branch Code / Financial Institution Number")}
                      aria-label={t(
                        "Branch Code / Financial Institution Number"
                      )}
                      value={method.branchCode}
                      readOnly
                    />
                  </div>
                )}
                {method.type === ZuoraPaymentMethodTypes.Bacs && (
                  <Input
                    label={t("Sort code")}
                    aria-label={t("Sort code")}
                    value={method.bankCode}
                    readOnly
                  />
                )}
                <Input
                  label={t(
                    card == null ? "Account holder name" : "Name on card"
                  )}
                  aria-label={t(
                    card == null ? "Account holder name" : "Name on card"
                  )}
                  value={
                    isAch
                      ? method.bankAccountName
                      : holder?.accountHolderName || ""
                  }
                  readOnly
                />
                <FormInput
                  control={control}
                  name="nickname"
                  label={t("Nickname (optional)")}
                />
                {card == null && !isAch && (
                  <>
                    <h3>{t("Contact and billing details")}</h3>
                    <FormInput
                      control={control}
                      name="accountHolderInfo.email"
                      label={t("Email")}
                      type="email"
                      isRequired
                    />
                    {isPad && (
                      <FormInput
                        control={control}
                        name="accountHolderInfo.phone"
                        label={t("Phone (optional)")}
                        type="tel"
                      />
                    )}
                    <FormInput
                      control={control}
                      name="accountHolderInfo.addressLine1"
                      label={t(isPad ? "Address line 1" : "Street number")}
                    />
                    <FormInput
                      control={control}
                      name="accountHolderInfo.addressLine2"
                      label={t(isPad ? "Address line 2" : "Street name")}
                    />
                    <div className="payment-field-pair">
                      <FormInput
                        control={control}
                        name="accountHolderInfo.city"
                        label={t("City")}
                        isRequired
                      />
                      <FormInput
                        control={control}
                        name="accountHolderInfo.zipCode"
                        label={t("Postal code")}
                        isRequired
                      />
                    </div>
                    {isPad && (
                      <FormInput
                        control={control}
                        name="accountHolderInfo.state"
                        label={t("State / province")}
                        isRequired
                      />
                    )}
                    {isSepa && (
                      <FormSelect
                        control={control}
                        name="accountHolderInfo.country"
                        label={t("Country")}
                        isRequired
                        options={Object.values(SepaCountries).map(
                          (country) => ({
                            value: country,
                            label: country,
                          })
                        )}
                      />
                    )}
                  </>
                )}
              </div>
              {card != null && <CardFeeNotice location={location} />}
              <section
                className="payment-removal"
                aria-labelledby="remove-payment-heading"
              >
                <h3 id="remove-payment-heading">
                  {t("Remove payment method")}
                </h3>
                <p>
                  {t("Your invoices and membership agreements remain active.")}
                </p>
                {isDefault && !isOnlyMethod && (
                  <>
                    <p>
                      {t(
                        "Choose another default payment method before removing this one."
                      )}
                    </p>
                    <label htmlFor="replacement-method">
                      {t("New default payment method")}
                    </label>
                    <Select
                      className="payment-single-select"
                      customClasses={{
                        control: "payment-select-control",
                        menu: "payment-select-menu",
                        singleValue: "payment-select-value",
                      }}
                      aria-label={t("New default payment method")}
                      value={
                        methods
                          .filter((value) => value.id === replacement)
                          .map((value) => ({
                            value: value.id,
                            label: paymentMethodLabel(value),
                          }))[0] ?? null
                      }
                      options={methods
                        .filter((value) => value.id !== method.id)
                        .map((value) => ({
                          value: value.id,
                          label: paymentMethodLabel(value),
                          isDisabled: isExpiredCard(value),
                        }))}
                      isOptionDisabled={(option) => Boolean(option.isDisabled)}
                      isDisabled={isBusy}
                      isSearchable={false}
                      isClearable={false}
                      onChange={(option) => setReplacement(option?.value ?? "")}
                    />
                    <Button
                      type="button"
                      variant={ButtonVariant.Outline}
                      isDisabled={isBusy || !replacement}
                      onClick={() => onMakeDefault(replacement)}
                    >
                      {t("Use as default")}
                    </Button>
                  </>
                )}
                {isOnlyMethod && account.autopay && (
                  <p className="payment-removal-restriction" role="status">
                    {t(
                      "This payment method is currently being used for Auto-Pay. Turn off Auto-Pay to remove it from your account."
                    )}
                  </p>
                )}
                {isOnlyMethod && !account.autopay && (
                  <p className="payment-removal-restriction">
                    {t(
                      "Without a saved payment method, arrange manual payment by each invoice due date."
                    )}
                  </p>
                )}
                <div className="payment-removal-actions">
                  <button
                    type="button"
                    className="payment-remove-button"
                    disabled={isBusy || blockRemoval}
                    onClick={() => setConfirmation("remove")}
                  >
                    {t("Remove payment method")}
                  </button>
                  {isOnlyMethod && account.autopay && (
                    <button
                      type="button"
                      className="payment-autopay-text-action"
                      disabled={isBusy}
                      onClick={onTurnOffAutoPay}
                    >
                      {t("Turn Auto-Pay Off")}
                    </button>
                  )}
                </div>
              </section>
              {error != null && (
                <p role="alert" className="payment-error">
                  {t(error.message)}
                </p>
              )}
            </fieldset>
          </DrawerContent>
          <DrawerActions>
            <div className="payment-editor-footer">
              <FormCheckbox
                control={control}
                name="defaultPayment"
                label={t("Set as default payment method")}
                isDisabled={isDefault || isBusy || isReadBlocked}
                subtitle={t(
                  isDefault
                    ? "Choose a different saved method to change your default."
                    : "Used for Auto-Pay when it is on. This does not turn Auto-Pay on."
                )}
              />
              <div className="payment-drawer-buttons">
                <Button
                  type="button"
                  variant={ButtonVariant.Outline}
                  isDisabled={isBusy}
                  onClick={close}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  type="submit"
                  isLoading={isBusy}
                  isDisabled={!isDirty || !isValid || isBusy || isReadBlocked}
                >
                  {t("Save Changes")}
                </Button>
              </div>
            </div>
          </DrawerActions>
        </form>
      </Drawer>
      <ConfirmationDialog
        isOpen={confirmation != null}
        isLoading={isBusy}
        onClose={() => setConfirmation(undefined)}
        cancelLabel={t("Keep editing")}
        confirmLabel={t(confirmationLabel)}
        title={t(confirmationTitle)}
        description={
          <>
            <p>
              {confirmation === "remove"
                ? `${label}. ${t(
                    isOnlyMethod
                      ? "You will have no saved payment methods. Auto-Pay will remain off."
                      : "Other saved payment methods and Auto-Pay will stay unchanged."
                  )}`
                : t(
                    confirmation === "discard"
                      ? "Your changes have not been saved."
                      : "Future automatic payments will use this method."
                  )}
            </p>
            {confirmation === "save" && card != null && (
              <CardFeeNotice location={location} />
            )}
          </>
        }
        onConfirm={async () => {
          if (confirmation === "discard") onClose();
          if (confirmation === "save" && pendingValues != null)
            await save(pendingValues);
          if (confirmation === "remove") {
            setConfirmation(undefined);
            if (await onRemove()) onClose();
          }
        }}
        onCancel={() => setConfirmation(undefined)}
      />
    </>
  );
}
